import { Form, Formik } from "formik";
import { Plus, Trash, Key, Edit, Shield } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import * as Yup from "yup";
import SideDrawer from "@/components/ui/SideDrawer";
import WelcomeView from "@/components/ui/WelcomeView";
import InputField from "@/components/form/InputField";
import { BasicTable } from "@/components/ui/BasicTable";
import DropdownMenu from "@/components/ui/DropdownMenu";
import { secretService } from "@/services/breeze/SecretService";
import {
  CreateSecretRequest,
  SecretResponse,
  UpdateSecretRequest,
} from "@/types/breeze/Secret";
import withPermissionRequired from "@/hoc/withPermissionRequired";
import { useConfirm } from "@/providers/ConfirmDialogProvider";
import { Button, Flex, Badge, Text, Box } from "@radix-ui/themes";
import PageTemplate from "@/components/page-template/PageTemplate";
import { formatDate } from "@/utils/date";

const SecretsPage = withPermissionRequired(
  "anythink_secrets:read",
  function SecretsPage() {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editingSecret, setEditingSecret] = useState<SecretResponse | null>(
      null,
    );
    const queryClient = useQueryClient();
    const confirm = useConfirm();

    const {
      data: secrets,
      isLoading: isSecretsLoading,
      error: secretsError,
    } = useQuery<SecretResponse[]>({
      queryKey: ["secrets"],
      queryFn: () => secretService.getSecrets(),
    });

    const createSecret = useMutation({
      mutationFn: (secret: CreateSecretRequest) =>
        secretService.createSecret(secret),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["secrets"] });
        closeDrawer();
      },
    });

    const updateSecret = useMutation({
      mutationFn: ({ key, data }: { key: string; data: UpdateSecretRequest }) =>
        secretService.updateSecret(key, data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["secrets"] });
        closeDrawer();
      },
    });

    const deleteSecret = useMutation({
      mutationFn: (key: string) => secretService.deleteSecret(key),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["secrets"] });
      },
    });

    const closeDrawer = () => {
      setIsDrawerOpen(false);
      setEditingSecret(null);
    };

    const openCreateDrawer = () => {
      setEditingSecret(null);
      setIsDrawerOpen(true);
    };

    const handleEdit = (secret: SecretResponse) => {
      setEditingSecret(secret);
      setIsDrawerOpen(true);
    };

    if (isSecretsLoading) {
      return <div>Loading...</div>;
    }

    if (secretsError) {
      return <div>Error loading secrets</div>;
    }

    return (
      <PageTemplate
        title="Secrets"
        description="Manage your application secrets and environment variables"
      >
        {secrets && secrets.length === 0 ? (
          <WelcomeView
            title="Secure Your Application"
            description="Store sensitive configuration securely with Anythink's secrets management. Keep API keys, database credentials, and other secrets safe while making them accessible to your application."
            quickStartSteps={[
              { text: "Add API keys and database credentials securely" },
              { text: "Manage environment-specific configuration" },
              { text: "Control user access to sensitive information" },
              { text: "Your secrets are encrypted and protected!" },
            ]}
            primaryAction={{
              text: "Add Your First Secret",
              icon: <Shield style={{ width: "16px", height: "16px" }} />,
              onClick: openCreateDrawer,
            }}
            padding={{
              top: "var(--space-3)",
              left: "0",
            }}
          />
        ) : (
          <>
            <Flex justify="end" align="center" mb="4">
              <Button onClick={openCreateDrawer} variant="outline" size="1">
                <Plus className="w-5 h-5" />
                Add Secret
              </Button>
            </Flex>

            <BasicTable
              columns={[
                {
                  header: "Key",
                  accessor: "key",
                  cellRenderer: (secret) => (
                    <Flex align="center" gap="2">
                      <Key size={16} style={{ color: "var(--gray-9)" }} />
                      <Text style={{ fontFamily: "var(--font-mono)" }} size="2">
                        {secret.key}
                      </Text>
                    </Flex>
                  ),
                },
                {
                  header: "Users",
                  accessor: "users",
                  cellRenderer: (secret) => (
                    <Badge variant="outline" size="1">
                      {secret.users.length}{" "}
                      {secret.users.length === 1 ? "user" : "users"}
                    </Badge>
                  ),
                },
                {
                  header: "Created At",
                  accessor: "created_at",
                  cellRenderer: (secret) => formatDate(secret.created_at),
                },
                {
                  header: "Updated At",
                  accessor: "updated_at",
                  cellRenderer: (secret) => formatDate(secret.updated_at),
                },
                {
                  header: "Actions",
                  cellRenderer: (secret) => (
                    <Box>
                      <DropdownMenu
                        items={[
                          {
                            label: "Edit",
                            icon: <Edit size={16} />,
                            onClick: () => handleEdit(secret),
                          },
                          {
                            label: "Delete",
                            icon: <Trash size={16} />,
                            onClick: async () => {
                              const confirmed = await confirm({
                                title: "Delete Secret",
                                message: `Are you sure you want to delete the secret "${secret.key}"? This action cannot be undone.`,
                                confirmText: "Delete",
                                cancelText: "Cancel",
                                variant: "danger",
                              });

                              if (confirmed) {
                                deleteSecret.mutate(secret.key);
                              }
                            },
                            className: "text-red-600",
                          },
                        ]}
                      />
                    </Box>
                  ),
                },
              ]}
              data={secrets?.sort((a, b) => a.key.localeCompare(b.key)) ?? []}
            />
          </>
        )}

        {/* Secret Drawer */}
        <SideDrawer
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          title={editingSecret ? "Edit Secret" : "Create New Secret"}
        >
          <Box p="4">
            <Formik
              initialValues={{
                key: editingSecret?.key || "",
                value: "",
              }}
              onSubmit={(values) => {
                if (editingSecret) {
                  // Edit mode
                  updateSecret.mutate({
                    key: editingSecret.key,
                    data: {
                      key: values.key,
                      value: values.value || undefined,
                    },
                  });
                } else {
                  // Create mode
                  createSecret.mutate({
                    key: values.key,
                    value: values.value,
                  });
                }
              }}
              validationSchema={Yup.object({
                key: Yup.string()
                  .required("Key is required")
                  .matches(
                    /^[A-Za-z0-9_-]*$/,
                    "Key must only container letters, numbers, underscores, and dashes",
                  ),
                value: editingSecret
                  ? Yup.string() // Optional for editing
                  : Yup.string().required("Value is required"), // Required for creating
              })}
            >
              <Form className="space-y-4">
                <InputField
                  name="key"
                  label="Key"
                  required
                  options={{ placeholder: "e.g., API_KEY, DATABASE_URL" }}
                />
                <InputField
                  name="value"
                  label={
                    editingSecret
                      ? "New Value (leave empty to keep current)"
                      : "Value"
                  }
                  required={!editingSecret}
                />
                <Flex gap="2" justify="end">
                  <Button type="button" variant="outline" onClick={closeDrawer}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={
                      editingSecret
                        ? updateSecret.isPending
                        : createSecret.isPending
                    }
                  >
                    {editingSecret ? "Update Secret" : "Create Secret"}
                  </Button>
                </Flex>
              </Form>
            </Formik>
          </Box>
        </SideDrawer>
      </PageTemplate>
    );
  },
);

export default SecretsPage;
